import { Injectable } from '@angular/core';
import { List } from 'immutable';
import { BodyList } from '../../../features-data/sub-models/body-list.model';
import { CanvasConfig } from '../../../features-data/sub-models/canvas-config.model';
import { Canvas } from '../../../features-data/sub-models/canvas.model';
import { NavigationBuilder } from '../../../navigation/navigation.builder';
import { ICheckmarkItem } from '../../../shared/components/checkmark-list/checkmark-list.component';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { IRouter } from '../../../shared/interfaces/router.interface';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { IIconUrl } from '../../../shared/components/icon-url/icon-url.interface';
import { CtaPlan } from '../../../features-data/sub-models/cta-plan';

export interface ICanvasData {
  imgSrc?: string;
  headerLabel: string;
  subtitle: string;
  planLevel: string;
  bodyText: string;
  growthHighlights: List<ICheckmarkItem>;
  primaryActionText: string;
  primaryActionRoute?: IRouter;
  secondaryActionText: string;
  secondaryActionRoute?: IRouter;
  actionLeadText: string;
  footerText: string;
  dismissible: boolean;
  primaryActionTrackingAdditionalProperties: any;
  secondaryActionTrackingAdditionalProperties: any;
  trackingObject: ITrackingObject;
  trackingScreenName: TrackingScreenNamesEnum;
  ctaPlans: List<CtaPlan>;
}

@Injectable()
export class GrowthDialogBuilder {
  constructor(
    private _navigationBuilder: NavigationBuilder,
    private _trackingObjectBuilder: TrackingObjectBuilder
  ) {}

  buildDialogData(canvas: Canvas): ICanvasData {
    const canvasConfig: CanvasConfig = canvas.canvasConfig;

    const growthHighlights: List<ICheckmarkItem> = canvasConfig.bodyList.map(
      (bodyList: BodyList): ICheckmarkItem => {
        let rightIcon: IIcon;
        let leftIconUrl: IIconUrl;

        if (bodyList.iconUrl) {
          leftIconUrl = {
            size: IconSizesEnum.medium,
            url: bodyList.iconUrl,
          };
        }
        if (bodyList.highlighted) {
          rightIcon = {
            name: 'premium-feature',
            size: IconSizesEnum.small,
            customClass: 'premium-icon',
          };
        }

        return {
          value: bodyList.message,
          rightIcon,
          leftIconUrl,
        };
      }
    ) as List<ICheckmarkItem>;

    const dialogData: ICanvasData = {
      imgSrc: canvasConfig.imageUri,
      headerLabel: canvasConfig.title,
      subtitle: canvasConfig.subtitle,
      planLevel: canvasConfig.header,
      bodyText: canvasConfig.body,
      growthHighlights: growthHighlights,
      primaryActionText: canvasConfig.cta1Label,
      secondaryActionText: canvasConfig.cta2Label,
      actionLeadText: canvasConfig.ctaInfo,
      dismissible: canvasConfig.dismissible,
      footerText: canvasConfig.footer,
      ctaPlans:
        canvasConfig.ctaPlans.size > 1
          ? canvasConfig.ctaPlans
          : List<CtaPlan>(),
      trackingObject: this._trackingObjectBuilder.buildGrowthCanvas(canvas),
      trackingScreenName: TrackingScreenNamesEnum.canvas,
      primaryActionTrackingAdditionalProperties: {
        target: canvasConfig.cta1Uri,
      },
      secondaryActionTrackingAdditionalProperties: {
        target: canvasConfig.cta2Uri,
      },
    };

    if (canvasConfig.cta1Uri) {
      const primaryActionDeepLinkProperties = this._navigationBuilder.buildDeepLinkProperties(
        canvasConfig.cta1Uri
      );
      const primaryActionRoute: IRouter = this._navigationBuilder.buildRouteFromDeepLinkProperties(
        primaryActionDeepLinkProperties
      );

      if (primaryActionRoute) {
        dialogData.primaryActionRoute = primaryActionRoute;
      }
    }

    if (canvasConfig.cta2Uri) {
      const secondaryActionDeepLinkProperties = this._navigationBuilder.buildDeepLinkProperties(
        canvasConfig.cta2Uri
      );
      const secondaryActionRoute: IRouter = this._navigationBuilder.buildRouteFromDeepLinkProperties(
        secondaryActionDeepLinkProperties
      );

      if (secondaryActionRoute) {
        dialogData.secondaryActionRoute = secondaryActionRoute;
      }
    }

    return dialogData;
  }

  buildRouteFromUri(uri: string): IRouter {
    const deepLinkProperties = this._navigationBuilder.buildDeepLinkProperties(
      uri
    );
    return this._navigationBuilder.buildRouteFromDeepLinkProperties(
      deepLinkProperties
    );
  }
}
